import { useState, useEffect } from 'react';
import { PlusIcon, CalculatorIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  TransactionType,
  CreateFeedStockTransactionDto
} from '../api/feedStockTransaction';
import { FeedStock as FeedStockType } from '../api/feedStock';
import DecimalInput from './ui/DecimalInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface FeedStockTransactionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFeedStockTransactionDto) => Promise<void>;
  feedStock: FeedStockType;
}

interface FormData {
  type: TransactionType;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  transactionDate: string;
  effectiveDate: string;
  referenceNumber: string;
  notes: string;
  metadata: {
    reason: string;
    location: string;
    operator: string;
    tags: string[];
    notes: string;
  };
}

const initialFormData: FormData = {
  type: TransactionType.RESTOCK,
  quantity: 0,
  unitPrice: 0,
  totalPrice: 0,
  transactionDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
  effectiveDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
  referenceNumber: '',
  notes: '',
  metadata: {
    reason: '',
    location: '',
    operator: '',
    tags: [],
    notes: ''
  }
};

export default function FeedStockTransactionForm({
  isOpen,
  onClose,
  onSubmit,
  feedStock
}: FeedStockTransactionFormProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTag, setNewTag] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...initialFormData,
        unitPrice: Number(feedStock.unitPrice) || 0,
        transactionDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
        effectiveDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
        metadata: {
          ...initialFormData.metadata,
          location: feedStock.location || ''
        }
      });
      setErrors({});
    }
  }, [isOpen, feedStock]);

  // Calculate total price when quantity or unit price changes
  useEffect(() => {
    const total = formData.quantity * formData.unitPrice;
    setFormData(prev => ({ ...prev, totalPrice: total }));
  }, [formData.quantity, formData.unitPrice]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.type) {
      newErrors.type = 'Transaction type is required';
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (formData.type === TransactionType.RESTOCK && formData.unitPrice <= 0) {
      newErrors.unitPrice = 'Unit price is required for restock transactions';
    }

    if (!formData.transactionDate) {
      newErrors.transactionDate = 'Transaction date is required';
    }

    // Validate that usage/waste doesn't exceed available stock
    if ((formData.type === TransactionType.USAGE || formData.type === TransactionType.WASTE) && 
        formData.quantity > feedStock.quantity) {
      newErrors.quantity = `Cannot ${formData.type.toLowerCase()} more than available stock (${feedStock.quantity} ${feedStock.unit})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    
    try {
      const submissionData: CreateFeedStockTransactionDto = {
        feedStockId: feedStock.id,
        type: formData.type,
        quantity: formData.quantity,
        unitPrice: formData.unitPrice > 0 ? formData.unitPrice : undefined,
        totalPrice: formData.totalPrice > 0 ? formData.totalPrice : undefined,
        transactionDate: formData.transactionDate,
        effectiveDate: formData.effectiveDate || undefined,
        referenceNumber: formData.referenceNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        metadata: {
          reason: formData.metadata.reason.trim() || undefined,
          location: formData.metadata.location.trim() || undefined,
          operator: formData.metadata.operator.trim() || undefined,
          tags: formData.metadata.tags.length > 0 ? formData.metadata.tags : undefined,
          notes: formData.metadata.notes.trim() || undefined
        }
      };

      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Error submitting transaction:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.metadata.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          tags: [...prev.metadata.tags, newTag.trim()]
        }
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        tags: prev.metadata.tags.filter(tag => tag !== tagToRemove)
      }
    }));
  };

  const getTransactionTypeDescription = (type: TransactionType) => {
    const descriptions = {
      [TransactionType.RESTOCK]: 'Add new stock from supplier or purchase',
      [TransactionType.USAGE]: 'Record feed consumption by birds',
      [TransactionType.ADJUSTMENT]: 'Adjust stock levels for corrections',
      [TransactionType.COUNT]: 'Physical stock count verification',
      [TransactionType.WASTE]: 'Record spoiled or wasted feed'
    };
    return descriptions[type] || '';
  };

  const getQuantityLabel = (type: TransactionType) => {
    const labels = {
      [TransactionType.RESTOCK]: 'Quantity Added',
      [TransactionType.USAGE]: 'Quantity Used',
      [TransactionType.ADJUSTMENT]: 'Adjustment Quantity',
      [TransactionType.COUNT]: 'Counted Quantity',
      [TransactionType.WASTE]: 'Quantity Wasted'
    };
    return labels[type] || 'Quantity';
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={`Add Transaction - ${feedStock.name}`} onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                          {/* Feed Stock Info */}
                          <div className="bg-slate-50/80 p-4 rounded-lg">
                            <h4 className="font-medium text-slate-900 mb-2">Feed Stock Information</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Current Stock:</span>
                                <span className="ml-2 font-medium">{feedStock.quantity} {feedStock.unit}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Unit Price:</span>
                                <span className="ml-2 font-medium">R{Number(feedStock.unitPrice).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Transaction Type */}
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Transaction Type *
                            </label>
                            <select
                              value={formData.type}
                              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as TransactionType }))}
                              className={`block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                errors.type ? 'border-red-300' : ''
                              }`}
                            >
                              {Object.values(TransactionType).map(type => (
                                <option key={type} value={type}>
                                  {type.charAt(0) + type.slice(1).toLowerCase()}
                                </option>
                              ))}
                            </select>
                            {formData.type && (
                              <p className="mt-1 text-xs text-slate-500">
                                {getTransactionTypeDescription(formData.type)}
                              </p>
                            )}
                            {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
                          </div>

                          {/* Quantity and Pricing */}
                          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                {getQuantityLabel(formData.type)} * ({feedStock.unit})
                              </label>
                              <DecimalInput
                                value={formData.quantity}
                                onChange={(value) => setFormData((prev) => ({ ...prev, quantity: value ?? 0 }))}
                                min={0}
                                placeholder="0.00"
                                error={errors.quantity}
                                inputClassName={`block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                  errors.quantity ? 'border-red-300' : ''
                                }`}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Unit Price (R)
                              </label>
                              <DecimalInput
                                value={formData.unitPrice}
                                onChange={(value) => setFormData((prev) => ({ ...prev, unitPrice: value ?? 0 }))}
                                min={0}
                                placeholder="0.00"
                                error={errors.unitPrice}
                                grouped
                                fractionDigits={2}
                                inputClassName={`block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                  errors.unitPrice ? 'border-red-300' : ''
                                }`}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Total Price (R)
                              </label>
                              <div className="relative">
                                <DecimalInput
                                  value={formData.totalPrice}
                                  onChange={(value) => setFormData((prev) => ({ ...prev, totalPrice: value ?? 0 }))}
                                  min={0}
                                  placeholder="0.00"
                                  grouped
                                  fractionDigits={2}
                                  inputClassName="block w-full input border-gray-300 shadow-sm sm:text-sm pr-10"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <CalculatorIcon className="h-4 w-4 text-slate-400" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Transaction Date *
                              </label>
                              <input
                                type="datetime-local"
                                value={formData.transactionDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, transactionDate: e.target.value }))}
                                className={`block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                  errors.transactionDate ? 'border-red-300' : ''
                                }`}
                              />
                              {errors.transactionDate && <p className="mt-1 text-sm text-red-600">{errors.transactionDate}</p>}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Effective Date
                              </label>
                              <input
                                type="datetime-local"
                                value={formData.effectiveDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                              />
                            </div>
                          </div>

                          {/* Reference and Location */}
                          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Reference Number
                              </label>
                              <input
                                type="text"
                                value={formData.referenceNumber}
                                onChange={(e) => setFormData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="Invoice #, PO #, etc."
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Location
                              </label>
                              <input
                                type="text"
                                value={formData.metadata.location}
                                onChange={(e) => setFormData(prev => ({ 
                                  ...prev, 
                                  metadata: { ...prev.metadata, location: e.target.value }
                                }))}
                                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="Storage location"
                              />
                            </div>
                          </div>

                          {/* Reason and Operator */}
                          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Reason
                              </label>
                              <input
                                type="text"
                                value={formData.metadata.reason}
                                onChange={(e) => setFormData(prev => ({ 
                                  ...prev, 
                                  metadata: { ...prev.metadata, reason: e.target.value }
                                }))}
                                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="Reason for transaction"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Operator
                              </label>
                              <input
                                type="text"
                                value={formData.metadata.operator}
                                onChange={(e) => setFormData(prev => ({ 
                                  ...prev, 
                                  metadata: { ...prev.metadata, operator: e.target.value }
                                }))}
                                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="Person responsible"
                              />
                            </div>
                          </div>

                          {/* Tags */}
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Tags
                            </label>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.metadata.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                >
                                  {tag}
                                  <button
                                    type="button"
                                    onClick={() => removeTag(tag)}
                                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500"
                                  >
                                    <XMarkIcon className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                className="flex-1 input border-gray-300 shadow-sm sm:text-sm"
                                placeholder="Add tag"
                              />
                              <button
                                type="button"
                                onClick={addTag}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Notes */}
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Notes
                            </label>
                            <textarea
                              rows={3}
                              value={formData.notes}
                              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                              className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                              placeholder="Additional notes about this transaction..."
                            />
                          </div>
        </BrandModalBody>
        <BrandModalFooter>
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Adding Transaction...' : 'Add Transaction'}
                    </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 